import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import LabeledValue from '../../../../components/common/LabeledValue'
import Modal from '../../../../components/common/Modal/Modal'
import ValidationBanner from '../../../../components/common/ValidationBanner'
import { ValidationMessages } from '../../../../components/common/Validation/validationMessageConfig'
import { BACKEND_UNAVAILABLE_MESSAGE } from '../../../../utils/apiError'
import {
  TimeOffRequestDetails,
  isVacationBid,
} from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import AddTimeToRequest from '../../CreateRequest/AddTimeToRequest'
import { EmbeddedVacationBidForm } from '../../CreateRequest/CreateVacationBidRequest'
import Loader from '../../../../components/common/Loader'
import { useCreateTimeOffRequest } from '../../../../hooks/useCreateTimeOffRequest'
import { usePrint } from '../../../../hooks/usePrint'
import {
  convertMMDDYYYYToYYYYMMDD,
  formatDateToMMDDYYYY,
} from '../../../../utils/date'
import { parseTimeString } from '../../../../utils/timeStringParser'
import {
  isRequestedDateFullDay,
  resolveRequestedDateDuration,
} from '../../../../utils/durationUtils'
import { shouldAddDateToFormDates } from '../../../../utils/dateSelectionUtils'
import { deleteTimeOffRequest } from '../../../../store/slices/Requestor/createNewRequestSlice'
import { RootState } from '../../../../store/store'
import { HoursValues } from '../../../../utils/balanceUtils'
import { formatHours } from '../../../../utils/formatters'
import { getEmployeeInfo } from '../../../../store/slices/Requestor/employeeInfoSlice'
import {
  TABLE_COLUMNS_WITH_ACTIONS,
  toPerRequestBreakdownEntries,
  getDisplayedOtherHoursTotal,
  isVacBidDayInOtherRequestsChecked,
  sortRowsByDate,
  type OtherRequestBreakdownEntry,
} from './constants'
/**
 * Maps a raw time-off allocation type code to the hours bucket used by the
 * Request Time Off form. Birthday ('B') and Vacation Bid ('A') are intentionally
 * excluded — they are not editable per-type hour entries in this form.
 */
const TYPE_CODE_TO_HOURS_KEY: Record<string, keyof HoursValues> = {
  V: 'vacation',
  F: 'float',
  D: 'flexible',
  O: 'other',
}

/**
 * Some validation messages need an explanatory sub-line under the banner
 * heading — matches the same heading+message treatment already used for
 * this message in CreateNewRequest and EditSubmittedRequest, which this
 * screen's banner otherwise renders as a bare heading only.
 */
const getValidationBannerSubMessage = (msg: string): React.ReactNode | undefined =>
  msg === ValidationMessages.REQUEST_DATES_EXCEED_8_DAY_GAP ? (
    <p className="p3 -book m-0!">
      If these dates represent more than one time off request, please submit
      them as separate requests.
    </p>
  ) : undefined

/**
 * The date-gap message renders at the larger (non-compact) banner size in
 * every existing Vacation Bid flow (CreateVacationBidRequest) — everything
 * else stays compact. Only applies to the Vacation Bid right panel here;
 * the regular (non-Vacation-Bid) panel matches CreateNewRequest/
 * EditSubmittedRequest instead, which keep this message compact too.
 */
const isExpandedVacBidValidationBanner = (msg: string): boolean =>
  msg === ValidationMessages.REQUEST_DATES_EXCEED_8_DAY_GAP

const isSoftBalanceWarning = (msg: string): boolean =>
  msg === ValidationMessages.BALANCE

const isBackendUnavailable = (msg: string): boolean =>
  msg === BACKEND_UNAVAILABLE_MESSAGE

const BALANCE_WARNING_HEADING = 'Total request exceeds your available balance.'

const getValidationBannerVariant = (msg: string): 'error' | 'info' =>
  isSoftBalanceWarning(msg) ? 'info' : 'error'

const getDraftValidationBannerHeading = (msg: string): React.ReactNode => {
  if (isBackendUnavailable(msg)) {
    return <p className="p1 -medium">Could not process the time off request</p>
  }
  return isSoftBalanceWarning(msg)
    ? <p className="p1 -medium">{BALANCE_WARNING_HEADING}</p>
    : <p className="p1 -medium">{msg}</p>
}

const getDraftValidationBannerMessage = (
  msg: string
): React.ReactNode | undefined => {
  const subMessage = getValidationBannerSubMessage(msg)
  if (subMessage) {
    return subMessage
  }

  if (isBackendUnavailable(msg)) {
    return (
      <p className="p3 -book m-0!">
        If the problem continues after re-logging in, email the National
        TORT Support team at{' '}
        <a
          href="mailto:NAT-TORT@kp.org"
          className="text-[#0074AD] hover:text-[#0074AD]! transition-none!"
        >
          NAT-TORT@kp.org
        </a>{' '}
        with your name, employee ID, NUID, computer screenshots, and a
        description of the issue.
      </p>
    )
  }

  if (isSoftBalanceWarning(msg)) {
    return <p className="p3 -book m-0!">{ValidationMessages.BALANCE}</p>
  }

  return <p className="p3 -book m-0!">{ValidationMessages.REVISE_REQUEST}</p>
}

interface DraftViewProps {
  data: TimeOffRequestDetails
  tableRows: any[]
  highlightedDates: string[]
}

const DraftView: React.FC<DraftViewProps> = ({
  data,
  tableRows,
  highlightedDates,
}) => {
  let navigate: any = null
  let dispatch: any = null

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate()
  } catch {
    // useNavigate may fail in test environments without Router context
  }

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    dispatch = useDispatch()
  } catch {
    // useDispatch may fail in test environments without Provider context
    dispatch = null
  }

  // `submitDraft` is not used here — AddTimeToRequest's own handleSaveDraft/
  // handleSubmitRequest call it internally (via preservedDatesPayload) for
  // the non-Vacation-Bid draft flow. DraftView only needs submitVacationBid
  // for the Vacation Bid flow, where saving is driven from this component.
  const { submitVacationBid } = useCreateTimeOffRequest()
  const { printRef, handlePrint } = usePrint()

  // CurrentRequestSection (rendered below) already dispatches getOtherReqDetails
  // for the viewed month, so this reads the same already-fetched data rather
  // than triggering a second, redundant fetch.
  const otherReqData = useSelector(
    (state: RootState) => state.otherReqDetails?.data
  )
  // Excludes this draft's own request id — the other-requests-for-month data
  // includes every request on a date, including this one, so without this
  // its own hours were counted as "other" hours against itself.
  const otherRequestHoursByDate = useMemo(() => {
    const result: Record<string, OtherRequestBreakdownEntry[]> = {}
    Object.entries(otherReqData ?? {}).forEach(([dateKey, detailsArray]) => {
      const parts = dateKey.split('-')
      if (parts.length !== 3) return
      const [year, month, day] = parts
      const mmddyyyy = `${month}/${day}/${year}`
      const otherRequestsOnly = Array.isArray(detailsArray)
        ? detailsArray.filter(
            (detail) => String(detail?.toReqId ?? '') !== String(data.requestId ?? '')
          )
        : []
      result[mmddyyyy] =
        otherRequestsOnly.length > 0
          ? toPerRequestBreakdownEntries(otherRequestsOnly)
          : []
    })
    return result
  }, [otherReqData, data.requestId])

  /** True when any allocation type is 'A' (Vacation Bid). */
  const isVacBid = isVacationBid(data)
  /** Only allow adding/editing time when the request is still in DRAFT status. */
  const isDraft = data.status === 'DRAFT'

  /**
   * The "Current Request" table's overlap column renders a checkbox for
   * Vacation Bid drafts (see renderCell below) — the header must say "Day in
   * Other Requests" to match, not the shared "Hours in Other Requests" label
   * used by non-Vacation-Bid requests.
   */
  const currentRequestTableColumns = isVacBid
    ? TABLE_COLUMNS_WITH_ACTIONS.map((col) =>
        col.key === 'hoursInOtherRequests'
          ? { ...col, label: 'Day in Other Requests' }
          : col
      )
    : TABLE_COLUMNS_WITH_ACTIONS

  /**
   * Remaps API-supplied "Vacation Bid" rows to "Vacation Bidding" for the
   * Current Request table, and normalizes hoursInOtherRequests into a real
   * boolean for the checkbox cell. Only applied on vacation bid drafts — all
   * other request types and all other status views are unaffected.
   *
   * Rows straight from buildTableRows (constants.ts) carry
   * hoursInOtherRequests as a formatted breakdown STRING (e.g. "0.00" or
   * "3.00 Oth") — always non-empty, so `!!row.hoursInOtherRequests` in the
   * checkbox renderCell below was always true regardless of actual overlap.
   * Rows produced in-session by EmbeddedVacationBidForm's buildReviewRows
   * already carry a real boolean, so those pass through unchanged.
   */
  const remapVacBidLabel = useCallback(
    (rows: any[]) =>
      isVacBid
        ? rows.map((r) => ({
            ...r,
            requestType:
              r.requestType === 'Vacation Bid' ? 'Vacation Bidding' : r.requestType,
            hoursInOtherRequests:
              typeof r.hoursInOtherRequests === 'boolean'
                ? r.hoursInOtherRequests
                : getDisplayedOtherHoursTotal(String(r.hoursInOtherRequests ?? '')) > 0,
          }))
        : rows,
    [isVacBid]
  )
  const isAdmin=location.pathname.includes('/admin');
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  // Dates currently being added/edited in the right-panel form. DraftView lets
  // the user accumulate MULTIPLE new dates (clicking toggles a date in/out)
  // before saving, instead of being limited to a single in-progress date.
  const [formDates, setFormDates] = useState<string[]>([])
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [localTableRows, setLocalTableRows] = useState(() =>
    remapVacBidLabel(tableRows)
  )
  const [isAddTimeReviewVisible, setIsAddTimeReviewVisible] = useState(false)
  const [exitAddTimeReviewSignal, setExitAddTimeReviewSignal] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingRemoveDate, setPendingRemoveDate] = useState<string | null>(
    null
  )
  // Validation messages bubbled up from the edit form so they are visible to the
  // user (previously these were swallowed and saving appeared to do nothing).
  const [validationMessages, setValidationMessages] = useState<string[] | null>(
    null
  )
  const [showCalendar, setShowCalendar] = useState(false)
  const [isAddingMoreDates, setIsAddingMoreDates] = useState(false)
  /**
   * Dates removed from the draft this session via the review table's "Remove
   * Date From Request" action (the minus icon), in MM/DD/YYYY form. `data` (and
   * the `highlightedDates`/committed dates derived from it) is a snapshot from
   * the initial load and only changes on a genuine backend refetch, so without
   * this the calendar kept showing a just-removed date as highlighted/committed
   * even though it had already disappeared from the Current Request table.
   */
  const [removedDates, setRemovedDates] = useState<string[]>([])

  // Get logged-in user NUID from Redux with fallback to sessionStorage
  const reduxNuid = useSelector((state: RootState) => {
    try {
      return state.employeeInfo?.timeOffBalance?.data?.employeeNUID || ''
    } catch {
      return ''
    }
  })
  // For DRAFT requests the API omits employee.fullName — fall back to Redux
  const empName = useSelector((state: RootState) => {
    try {
      return state.employeeInfo?.employeeInfo?.data?.employeeName || ''
    } catch {
      return ''
    }
  })
  const empNuid =
    reduxNuid ||
    sessionStorage.getItem('userId') ||
    sessionStorage.getItem('nuid') ||
    ''
  const displayName = empName || ''
  const employeeInfo = useSelector(
    (state: RootState) => state.employeeInfo.employeeInfo.data
  )
  // Derive isExempt from employee type: 'S' indicates salaried/exempt employee.
  // employeeInfo is fetched with x-emp-nuid, so in Manual Time Off it resolves
  // to the selected employee rather than the logged-in admin.
  const isExempt = useSelector((state: RootState) => {
    try {
      const empType =
        state.employeeInfo?.employeeInfo?.data?.empType ??
        state.profile?.data?.employee?.empType
      return empType === 'S'
    } catch {
      return false
    }
  })

  useEffect(() => {
    if (!employeeInfo && dispatch) {
      dispatch(getEmployeeInfo())
    }
  }, [dispatch, employeeInfo])

  /**
   * Previously saved hours keyed by date (MM/DD/YYYY) so the edit form pre-fills
   * the user's existing values instead of showing 0.
   *
   * Each date can carry MULTIPLE allocations (e.g. Vacation 4h + Float 4h), so
   * we distribute every allocation into its own hours bucket. Using the
   * flattened `requestType` (first allocation only) + `requestedHours` (sum of
   * all allocations) would incorrectly collapse the total under a single type
   * (typically Vacation). When per-allocation detail is unavailable we fall back
   * to that flattened representation.
   */
  const initialHoursByDate = useMemo(() => {
    const map: Record<string, HoursValues> = {}
    ;(data.requestedDates ?? []).forEach((rowDetail) => {
      const dateKey = formatDateToMMDDYYYY(rowDetail.date)
      if (!dateKey) return

      const bucket: HoursValues = {
        vacation: '0',
        float: '0',
        flexible: '0',
        other: '0',
      }
      const allocations = rowDetail.timeOffAllocations ?? []

      if (allocations.length > 0) {
        // Keep each allocation under its own type, summing same-type entries.
        allocations.forEach((alloc) => {
          const hoursKey =
            TYPE_CODE_TO_HOURS_KEY[String(alloc.toTypeCd).toUpperCase()]
          if (!hoursKey) return
          const running = Number.parseFloat(bucket[hoursKey]) || 0
          bucket[hoursKey] = String(running + (Number(alloc.hours) || 0))
        })
      } else {
        // Fallback: flattened primary type + total hours.
        const hoursKey =
          TYPE_CODE_TO_HOURS_KEY[String(rowDetail.requestType).toUpperCase()]
        if (!hoursKey) return
        bucket[hoursKey] = String(rowDetail.requestedHours ?? '0')
      }

      map[dateKey] = bucket
    })
    localTableRows.forEach((row) => {
      if (!row.date || row.id === 'total' || map[row.date]) return
      const reviewHours = row.reviewHoursValues ?? null
      map[String(row.date)] = reviewHours
        ? { ...reviewHours }
        : {
            vacation: String(row.requestedHours ?? '0'),
            float: '0',
            flexible: '0',
            other: '0',
          }
    })
    return map
  }, [data.requestedDates, localTableRows])

  /**
   * Previously saved vacation-bid hours keyed by date (MM/DD/YYYY) so the
   * embedded vacation-bid edit form pre-fills the user's existing value instead
   * of 0. Vacation-bid allocations use type code 'A' with the hours total on
   * `requestedHours`.
   *
   * Prioritizes `localTableRows` over API data to ensure newly-entered hours are
   * used when editing a date added in the current session (before API save).
   * This fixes Issue #3 where editing a newly-added vacation bid date would show
   * stale hours instead of the user-entered value.
   */
  const initialVacHoursByDate = useMemo(() => {
    const map: Record<string, string> = {}
    // First, load API-persisted vacation bid dates
    ;(data.requestedDates ?? []).forEach((rowDetail) => {
      if (String(rowDetail.requestType).toUpperCase() !== 'A') return
      const dateKey = formatDateToMMDDYYYY(rowDetail.date)
      if (!dateKey) return
      map[dateKey] = String(rowDetail.requestedHours ?? '0')
    })
    // Then, add/override with locally-accumulated rows (added this session)
    // For vacation bids, we prefer locally-reviewed hours over API data to handle
    // the case where a user adds a date, then immediately tries to edit it before
    // the API has persisted the change.
    localTableRows.forEach((row) => {
      if (!row.date || row.id === 'total') return
      // For vacation bid rows, use requestedHours from the row (already set by handleReviewReady)
      const hoursValue = row.requestedHours ?? '0'
      map[String(row.date)] = String(hoursValue)
    })
    return map
  }, [data.requestedDates, localTableRows])

  /**
   * Attempts to parse a valid numeric rank from a table row's choiceRank field.
   * Returns the parsed rank if valid, or null if missing/invalid.
   */
  const tryParseRankFromRow = (row: any): number | null => {
    const rowRank = row?.choiceRank
    if (rowRank == null || rowRank === 'Not applicable') return null
    const parsed = Number.parseInt(String(rowRank), 10)
    return Number.isNaN(parsed) ? null : parsed
  }

  const activeVacationBidRank = useMemo(() => {
    const activeDate = formDates.length === 1 ? formDates[0] : null

    if (activeDate) {
      const localRow = localTableRows.find(
        (row) => row.id !== 'total' && String(row.date) === activeDate
      )
      const rank = tryParseRankFromRow(localRow)
      if (rank !== null) return rank
    }

    // Use rank from any local row so current-session rank edits are reflected
    // when adding new dates, instead of always reading the stale API rank.
    for (const row of localTableRows) {
      if (row.id === 'total' || !row.date) continue
      const rank = tryParseRankFromRow(row)
      if (rank !== null) return rank
    }

    return data.requestRank ?? null
  }, [data.requestRank, formDates, localTableRows])

  /**
   * Previously saved "Other" time-off subtype code (e.g. 'OTH_JD') keyed by date
   * (MM/DD/YYYY) so the edit form pre-fills the Other-time-off dropdown instead
   * of showing an empty selection. Derived from the date's allocation whose type
   * code is 'O', falling back to the row-level `toSubTypeCd` when per-allocation
   * detail is unavailable.
   */
  const initialOtherSubTypeByDate = useMemo(() => {
    const map: Record<string, string> = {}
    ;(data.requestedDates ?? []).forEach((rowDetail) => {
      const dateKey = formatDateToMMDDYYYY(rowDetail.date)
      if (!dateKey) return
      const otherAlloc = (rowDetail.timeOffAllocations ?? []).find(
        (alloc) =>
          String(alloc.toTypeCd).toUpperCase() === 'O' && alloc.toSubTypeCd
      )
      const subType = otherAlloc?.toSubTypeCd ?? rowDetail.toSubTypeCd ?? null
      if (subType) map[dateKey] = String(subType)
    })
    localTableRows.forEach((row) => {
      if (!row.date || row.id === 'total' || map[row.date]) return
      if (row.reviewOtherSubType) {
        map[String(row.date)] = String(row.reviewOtherSubType)
      }
    })
    return map
  }, [data.requestedDates, localTableRows])

  /**
   * Previously saved Full/Partial Day duration keyed by date (MM/DD/YYYY) so the
   * edit form restores the correct duration radio when a saved date is reopened.
   * Resolved from the authoritative `durationFlag` (never the display `times`
   * string) so a Full Day that carries a time window is not mistaken for Partial
   * Day — which previously reverted saved Partial Day dates to Full Day on edit.
   */
  const initialDurationByDate = useMemo(() => {
    const map: Record<string, 'Full Day' | 'Partial Day'> = {}
    ;(data.requestedDates ?? []).forEach((rowDetail) => {
      const dateKey = formatDateToMMDDYYYY(rowDetail.date)
      if (!dateKey) return
      map[dateKey] = resolveRequestedDateDuration(rowDetail)
    })
    localTableRows.forEach((row) => {
      if (!row.date || row.id === 'total' || map[row.date]) return
      map[String(row.date)] = row.reviewDuration ?? 'Full Day'
    })
    return map
  }, [data.requestedDates, localTableRows])

  /**
   * Reads a from/to time field that may be a dropdown option object
   * (`{ value: 'AM' }`, as stored on a review-table row's `reviewTime`) or
   * already a plain string. Every consumer downstream of `initialTimeByDate`
   * (preservedDatesPayload, the submission payload builder) expects plain
   * strings, so this must run before a value is stored in that map.
   */
  const readTimeFieldValue = (field: unknown, fallback: string): string => {
    if (field && typeof field === 'object') {
      return (field as { value?: string }).value || fallback
    }
    return (field as string) || fallback
  }

  /**
   * Previously saved partial-day time window keyed by date (MM/DD/YYYY) so the
   * edit form restores the start/end time pickers when a saved Partial Day date
   * is reopened. Only populated for dates with a parseable partial-day window.
   */
  const initialTimeByDate = useMemo(() => {
    const map: Record<
      string,
      {
        from: { hour: string; minute: string; ampm: string }
        to: { hour: string; minute: string; ampm: string }
      }
    > = {}
    ;(data.requestedDates ?? []).forEach((rowDetail) => {
      if (isRequestedDateFullDay(rowDetail)) return
      const dateKey = formatDateToMMDDYYYY(rowDetail.date)
      if (!dateKey) return
      const parsed = parseTimeString(String(rowDetail.times ?? ''))
      if (parsed) map[dateKey] = parsed
    })
    localTableRows.forEach((row) => {
      if (!row.date || row.id === 'total' || map[row.date]) return
      if (row.reviewTime) {
        // `reviewTime` comes from AddTimeToRequest's review table and carries
        // dropdown-option objects (`{ value: 'AM' }`), not plain strings —
        // normalize each field or every date-time consumer downstream that
        // expects a string (e.g. payloadBuilder's `(ampm || 'AM').toUpperCase()`)
        // breaks with "toUpperCase is not a function".
        map[String(row.date)] = {
          from: {
            hour: readTimeFieldValue(row.reviewTime.from?.hour, '1'),
            minute: readTimeFieldValue(row.reviewTime.from?.minute, '00'),
            ampm: readTimeFieldValue(row.reviewTime.from?.ampm, 'AM'),
          },
          to: {
            hour: readTimeFieldValue(row.reviewTime.to?.hour, '1'),
            minute: readTimeFieldValue(row.reviewTime.to?.minute, '00'),
            ampm: readTimeFieldValue(row.reviewTime.to?.ampm, 'AM'),
          },
        }
        return
      }
      const parsed = parseTimeString(String(row.times ?? ''))
      if (parsed) map[String(row.date)] = parsed
    })
    return map
  }, [data.requestedDates, localTableRows])

  /**
   * This draft's own committed dates that are Partial Day, keyed to their
   * existing time window(s) (raw ISO strings, matching the shape AddTimeToRequest
   * already expects for otherPartialDayDates). A Partial Day date always has
   * unused hours left in the day by definition, so these are exactly the
   * committed dates that should stay clickable during "Add date to request" —
   * reusing the same prop lets AddTimeToRequest force Partial Day and block a
   * new time window that overlaps the existing one for that date.
   */
  const ownPartialDayDates = useMemo(() => {
    const map: Record<
      string,
      Array<{ startTime?: string | null; endTime?: string | null }>
    > = {}
    ;(data.requestedDates ?? []).forEach((rowDetail) => {
      if (isRequestedDateFullDay(rowDetail)) return
      const dateKey = formatDateToMMDDYYYY(rowDetail.date)
      if (!dateKey || !rowDetail.startTime || !rowDetail.endTime) return
      if (!map[dateKey]) map[dateKey] = []
      map[dateKey].push({
        startTime: rowDetail.startTime,
        endTime: rowDetail.endTime,
      })
    })
    return map
  }, [data.requestedDates])

  /** Committed dates exempt from the Add More calendar block — see ownPartialDayDates above. */
  const partialDayDatesWithRoom = useMemo(
    () => Object.keys(ownPartialDayDates),
    [ownPartialDayDates]
  )

  /**
   * Partial-day time window(s) from OTHER, already-submitted requests (not
   * this draft's own) — same `otherReqData` CurrentRequestSection already
   * fetches for the viewed month, this draft's own request id excluded.
   *
   * Regression: a brand-new date added via "Add date to request" that
   * already carries a different request's partial-day window never appeared
   * in `ownPartialDayDates` (that only tracks THIS draft's saved dates), so
   * `isFullDayDisabled` in AddTimeToRequest always saw an empty entry for it
   * and left the Full Day radio selectable — letting a full-day pick collide
   * with the existing partial-day request. Mirrors CreateNewRequest's
   * `otherPartialDayDates`, built the same way from the same data.
   */
  const otherRequestsPartialDayDates = useMemo(() => {
    const map: Record<
      string,
      Array<{ startTime?: string | null; endTime?: string | null }>
    > = {}
    Object.entries(otherReqData ?? {}).forEach(([dateKey, detailsArray]) => {
      if (!Array.isArray(detailsArray) || detailsArray.length === 0) return
      const otherRequestsOnly = detailsArray.filter(
        (detail: any) =>
          String(detail?.toReqId ?? '') !== String(data.requestId ?? '')
      )
      const ranges = otherRequestsOnly
        .filter((detail: any) => !isRequestedDateFullDay(detail))
        .filter((detail: any) => detail?.startTime && detail?.endTime)
        .map((detail: any) => ({
          startTime: detail.startTime,
          endTime: detail.endTime,
        }))
      if (ranges.length === 0) return
      const parts = dateKey.split('-')
      if (parts.length !== 3) return
      const [year, month, day] = parts
      map[`${month}/${day}/${year}`] = ranges
    })
    return map
  }, [otherReqData, data.requestId])

  /**
   * `ownPartialDayDates`, minus the date currently open for a plain edit,
   * merged with `otherRequestsPartialDayDates` so both this draft's own
   * saved partial dates AND other requests' partial dates force Partial Day
   * / block overlaps for AddTimeToRequest.
   *
   * AddTimeToRequest's overlap check (performPartialDayOverlapCheck) compares
   * the user's selected time window against every range in `otherPartialDayDates`
   * for the active date. During a genuine single-date edit (not "Add date to
   * request"), `ownPartialDayDates[editDate]` holds nothing but that same
   * date's own previously-saved window — comparing the edit against itself
   * always "overlaps" and incorrectly blocked every partial-day edit with
   * "Partial day requests overlap." (a request can only have one partial-day
   * window per date, so that entry can never represent a second, genuinely
   * conflicting window). Other requests' windows are unaffected by that
   * self-comparison and always stay enforced.
   *
   * During "Add date to request" the existing window for a date must still be
   * enforced (a new window added to an already-committed partial-day date must
   * not overlap it), so this only strips the own-date key outside that flow.
   */
  const otherPartialDayDatesForForm = useMemo(() => {
    const activeEditDate = formDates.at(-1) ?? null
    const ownPart =
      isAddingMoreDates || !activeEditDate || !ownPartialDayDates[activeEditDate]
        ? ownPartialDayDates
        : (() => {
            const { [activeEditDate]: _omitted, ...rest } = ownPartialDayDates
            return rest
          })()

    const merged: Record<
      string,
      Array<{ startTime?: string | null; endTime?: string | null }>
    > = { ...otherRequestsPartialDayDates }
    Object.entries(ownPart).forEach(([date, ranges]) => {
      merged[date] = merged[date] ? [...merged[date], ...ranges] : ranges
    })
    return merged
  }, [ownPartialDayDates, otherRequestsPartialDayDates, isAddingMoreDates, formDates])

  /**
   * The draft's already-known dates OTHER than the one currently being edited,
   * shaped for the submission payload (YYYY-MM-DD keys). DraftView edits a single
   * calendar date at a time, so without re-sending these the backend would
   * replace the whole draft with just the edited date — silently dropping every
   * other day.
   *
   * "Already-known" means every date currently in `localTableRows` — the
   * "Current Request" table, which starts from `data.requestedDates` (the
   * API-persisted dates) and is kept in sync with every local add
   * (handleReviewReady) or remove (handleRemoveDateFromDraft) made during
   * the session. Sourcing this from `data.requestedDates` directly (in
   * addition to `localTableRows`) would silently resurrect a date the user
   * just removed via the table's remove icon, since that snapshot is
   * untouched until an actual save. Values are read from `initialHoursByDate`
   * / `initialOtherSubTypeByDate` / `initialDurationByDate` / `initialTimeByDate`,
   * which already correctly merge API-sourced and locally-added-but-unsaved rows
   * (via each row's `reviewHoursValues`/`reviewDuration`/etc.) for the same
   * reason — reusing them here keeps a single source of truth instead of a second,
   * API-only reconstruction that silently missed not-yet-saved sibling dates.
   *
   * Computed even when `formDates` is empty (no date currently open in the
   * form) so that landing on the draft and clicking Save as Draft/Submit
   * Request straight away — without opening any date first — still sends
   * every date already in the table instead of an empty payload.
   */
  const preservedDatesPayload = useMemo(() => {
    const activeApiDates = new Set(
      formDates.map((d) => convertMMDDYYYYToYYYYMMDD(d))
    )

    const allKnownMmddyyyy = new Set<string>()
    localTableRows.forEach((row) => {
      if (row.id !== 'total' && row.date) allKnownMmddyyyy.add(String(row.date))
    })

    const selectedDates: string[] = []
    const perDateHoursValues: Record<string, HoursValues> = {}
    const perDateDurations: Record<string, 'Full Day' | 'Partial Day'> = {}
    const perDateTimes: Record<
      string,
      {
        from: { hour?: string; minute?: string; ampm?: string }
        to: { hour?: string; minute?: string; ampm?: string }
      }
    > = {}
    const perDateOtherSubTypeValues: Record<string, string> = {}

    allKnownMmddyyyy.forEach((mmddyyyy) => {
      const apiDate = convertMMDDYYYYToYYYYMMDD(mmddyyyy)
      // Skip dates the form is currently editing — their values come from the form.
      if (!apiDate || activeApiDates.has(apiDate)) return

      selectedDates.push(apiDate)
      perDateHoursValues[apiDate] = initialHoursByDate[mmddyyyy] ?? {
        vacation: '0',
        float: '0',
        flexible: '0',
        other: '0',
      }
      // Preserve the saved Other subtype for this date so editing a different
      // date in the same draft never overwrites it with the active session's
      // global leaveTypeSelected value.
      const savedSubType = initialOtherSubTypeByDate[mmddyyyy]
      if (savedSubType) perDateOtherSubTypeValues[apiDate] = savedSubType

      // Reconstruct the saved duration/time window so a preserved date keeps its
      // own Full/Partial Day setting instead of inheriting the edited date's.
      const duration = initialDurationByDate[mmddyyyy] ?? 'Full Day'
      perDateDurations[apiDate] = duration
      if (duration === 'Partial Day') {
        const savedTime = initialTimeByDate[mmddyyyy]
        if (savedTime) {
          perDateTimes[apiDate] = {
            from: { ...savedTime.from },
            to: { ...savedTime.to },
          }
        }
      }
    })

    if (selectedDates.length === 0) return undefined
    return {
      selectedDates,
      perDateHoursValues,
      perDateDurations,
      perDateTimes,
      perDateOtherSubTypeValues,
    }
  }, [
    formDates,
    localTableRows,
    initialHoursByDate,
    initialOtherSubTypeByDate,
    initialDurationByDate,
    initialTimeByDate,
  ])

  /**
   * Calendar highlight set including the in-progress `formDates`, minus any
   * date removed this session (see `removedDates` above) — `highlightedDates`
   * itself is a static snapshot from the initial load and never drops a
   * removed date on its own. Adding the actively-edited dates keeps them
   * styled as part of the (draft) request instead of rendering with the plain
   * "selected"/submitted color before save.
   */
  const displayHighlightedDates = useMemo(() => {
    const base = highlightedDates.filter((date) => !removedDates.includes(date))
    return formDates.length > 0
      ? Array.from(new Set([...base, ...formDates]))
      : base
  }, [highlightedDates, formDates, removedDates])

  // Dates already part of the saved request — excludes any removed this
  // session (see `removedDates` above) so a just-removed date stops being
  // treated as committed (non-deselectable, calendar-highlighted) before the
  // next backend refetch confirms the removal.
  const committedDates = useMemo(() => {
    return (data.requestedDates ?? [])
      .map((rowDetail) => formatDateToMMDDYYYY(rowDetail.date))
      .filter(
        (date): date is string => Boolean(date) && !removedDates.includes(date)
      )
  }, [data.requestedDates, removedDates])

  // Any date currently present in the Current Request table (saved or newly
  // reviewed) should behave like an entered date: editable on click, not
  // deselectable.
  const enteredTableDates = useMemo(() => {
    return localTableRows
      .filter((row) => row.id !== 'total' && Boolean(row.date))
      .map((row) => String(row.date))
  }, [localTableRows])

  const nonDeselectableDates = useMemo(() => {
    return Array.from(new Set([...committedDates, ...enteredTableDates]))
  }, [committedDates, enteredTableDates])

  // Re-sync if parent refreshes data (e.g., after API reload)
  // Re-sync if parent refreshes data (e.g., after API reload), but preserve
  // unsaved local rows so newly-added draft dates do not disappear.
  //
  // Keyed on `data.requestedDates` rather than `tableRows`: the parent
  // (TimeOffRequestDetail) recomputes `tableRows` inline on every render,
  // including renders triggered by unrelated `otherReqDetails` Redux updates
  // (e.g. the calendar re-fetching "other requests" for the viewed month
  // while the user is mid-edit). Depending on `tableRows` directly meant this
  // effect re-ran on those incidental renders too, and since `incoming` is
  // rebuilt from the still-unchanged (not-yet-saved) `data`, it always won
  // the merge for that date — silently discarding the hours the user had
  // just entered in the edit form before they got a chance to save. Using
  // `data.requestedDates` only re-syncs on a genuine backend reload.
  useEffect(() => {
    const incoming = remapVacBidLabel(tableRows)

    setLocalTableRows((prev) => {
      const prevData = prev.filter((row) => row.id !== 'total')
      const incomingData = incoming.filter((row) => row.id !== 'total')

      const mergedByDate = new Map<string, any>()

      incomingData.forEach((row) => {
        if (!row.date) return
        mergedByDate.set(String(row.date), row)
      })

      // Keep local-only rows (newly added this session) if backend data
      // has not returned them yet.
      prevData.forEach((row) => {
        if (!row.date) return
        const dateKey = String(row.date)
        if (!mergedByDate.has(dateKey)) {
          mergedByDate.set(dateKey, row)
        }
      })

      // Sort so the Current Request table always reads in ascending date
      // order, regardless of API order or which rows were kept locally.
      const mergedDataRows = sortRowsByDate(Array.from(mergedByDate.values()))
      const totalHours = mergedDataRows.reduce(
        (sum, row) => sum + Number(row.requestedHours || 0),
        0
      )
      // Vacation Bid rows carry a checkbox (boolean), not hours to sum — legacy
      // leaves this cell blank on the total row for Vacation Bid requests.
      const totalOtherDisplay = isVacBid
        ? ''
        : formatHours(
            mergedDataRows.reduce(
              (sum, row) => sum + getDisplayedOtherHoursTotal(String(row.hoursInOtherRequests || '')),
              0
            )
          )

      return [
        ...mergedDataRows,
        {
          id: 'total',
          requestType: 'Total Hours',
          date: '',
          day: '',
          times: '',
          requestedHours: formatHours(totalHours),
          hoursInOtherRequests: totalOtherDisplay,
        },
      ]
    })
  }, [data.requestedDates, remapVacBidLabel, isVacBid])

  /**
   * Re-syncs ONLY the `hoursInOtherRequests` cell whenever the parent's
   * `tableRows` prop changes — deliberately narrower than the row-sync effect
   * above, which intentionally does NOT depend on `tableRows` (see its
   * comment) to avoid clobbering an in-progress edit's other fields.
   *
   * Regression: TimeOffRequestDetail computes `tableRows` from
   * `otherReqDetails`, which is fetched asynchronously and is still `{}` on
   * this component's first paint — so every row's "Hours in Other Requests"
   * starts out as the buildTableRows fallback ('0.00'). Once that fetch
   * resolves, TimeOffRequestDetail re-renders with the real per-date
   * breakdown and passes a corrected `tableRows` down, but with nothing
   * depending on it, `localTableRows` never picked up the correction — every
   * date's overlap column stayed frozen at '0.00' even when the freshly
   * computed value (visible in `tableRows`) was correct. This only ever
   * touches `hoursInOtherRequests`, so it can't discard unsaved edits to any
   * other field.
   */
  useEffect(() => {
    const incoming = remapVacBidLabel(tableRows)
    const hoursByDate = new Map<string, string | boolean>()
    incoming.forEach((row) => {
      if (row.id === 'total' || !row.date) return
      hoursByDate.set(String(row.date), row.hoursInOtherRequests)
    })
    if (hoursByDate.size === 0) return

    setLocalTableRows((prev) => {
      let changed = false
      const nextRows = prev.map((row) => {
        if (row.id === 'total' || !row.date) return row
        const freshValue = hoursByDate.get(String(row.date))
        if (freshValue === undefined || freshValue === row.hoursInOtherRequests) {
          return row
        }
        changed = true
        return { ...row, hoursInOtherRequests: freshValue }
      })
      if (!changed) return prev

      const totalOtherDisplay = isVacBid
        ? ''
        : formatHours(
            nextRows
              .filter((row) => row.id !== 'total')
              .reduce(
                (sum, row) =>
                  sum + getDisplayedOtherHoursTotal(String(row.hoursInOtherRequests || '')),
                0
              )
          )

      return nextRows.map((row) =>
        row.id === 'total' ? { ...row, hoursInOtherRequests: totalOtherDisplay } : row
      )
    })
  }, [tableRows, remapVacBidLabel, isVacBid])

  useEffect(() => {
    if (isAddTimeReviewVisible) setShowCalendar(false)
  }, [isAddTimeReviewVisible])

  /**
   * Handles calendar date selection. Manages selection logic for different modes:
   * - Normal edit mode: click to edit single date
   * - Add More Dates mode: click to accumulate new dates (blocks existing dates for vacation bids)
   * - Review mode: re-clicking selected date opens form in edit mode
   *
   * Fixes Issue #4: For vacation bids during "Add More Dates", blocks all calendar clicks
   * on committed dates. Users must use the table's edit button to modify existing dates.
   */
  const handleDateClick = (date: string) => {
    setShowCalendar(true)
    setValidationMessages(null)

    const isSelected = formDates.includes(date)
    const isCommitted = nonDeselectableDates.includes(date)

    // ISSUE #4 FIX: For vacation bids, block ALL calendar clicks on committed dates
    // during "Add More Dates" mode. Only table edit button should allow editing existing dates.
    if (isVacBid && isAddingMoreDates && isCommitted) {
      return
    }

    if (isCommitted && isSelected) {
      setFormDates([date])
      setIsEditPanelOpen(true)

      if (isAddTimeReviewVisible) {
        setExitAddTimeReviewSignal((c) => c + 1)
      }

      return
    }

    // In review/edit interaction, clicking a selected date should always open/switch form edit mode.
    // When adding more dates, existing dates should not enter edit mode.
    // Only allow selecting new dates.
    if (isAddingMoreDates && isSelected && isCommitted) {
      return // still block committed dates (for non-vacation-bid requests)
    }
    if (isAddingMoreDates && isSelected && !isCommitted) {
      // clicking an already-selected new date → enter edit mode for it
      setIsEditPanelOpen(true)
      setFormDates((prev) => prev.filter((d) => d !== date))
      setValidationMessages(null)
      return
    }

    if (isAddTimeReviewVisible && isSelected) {
      setFormDates([date])
      setIsEditPanelOpen(true)
      setExitAddTimeReviewSignal((c) => c + 1)
      return
    }

    if (!isSelected) {
      setFormDates((prev) => {
        if (isAddingMoreDates) {
          return shouldAddDateToFormDates(date, prev) ?? prev
        }

        return [date]
      })

      setIsEditPanelOpen(true)
    }
  }

  const handleFormClose = () => {
    // Cancel should fully revert this in-session edit state back to the
    // initial draft snapshot currently provided by parent `tableRows`.
    setLocalTableRows(remapVacBidLabel(tableRows))
    setRemovedDates([])
    setPendingRemoveDate(null)
    setIsAddTimeReviewVisible(false)
    setExitAddTimeReviewSignal((count) => count + 1)
    setEditSessionId((count) => count + 1)
    setIsEditPanelOpen(false)
    setValidationMessages(null)
    setShowCalendar(false)
    setIsAddingMoreDates(false)
    // The Vacation Bid right panel (renderRightPanel) decides whether to show
    // the edit form purely from formDates.length, not isEditPanelOpen — so
    // without clearing this too, cancelling would hide the calendar but leave
    // the stale form (with the cancelled date's hours/rank) still on screen.
    setFormDates([])
  }

  const handleDeleteRequest = useCallback(async () => {
    if (!data.requestId || !dispatch) return

    setIsDeleting(true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await dispatch(
        deleteTimeOffRequest({ requestId: data.requestId, empNuid }) as any
      )
      if (result?.meta?.requestStatus === 'fulfilled') {
        setShowDeleteModal(false)
        navigate?.(isAdmin?'/admin/requestor':'/requestor')
      }
      // On failure the modal stays open so the user can retry or cancel.
    } catch {
      // Delete failed; keep modal open so user can retry.
    } finally {
      setIsDeleting(false)
    }
  }, [data.requestId, empNuid, dispatch, navigate])

  type ReviewRow = {
    id: string
    requestType: string
    date: string
    day: string
    times: string
    requestedHours: string
    /** Vacation Bid rows carry a boolean (checkbox); normal rows carry a formatted hours string. */
    hoursInOtherRequests: string | boolean
  }

  /**
   * Merges incoming rows from either AddTimeToRequest or EmbeddedVacationBidForm into the table.
   * Handles deduplication and prevents duplicate entries when editing existing dates.
   *
   * Fixes Issue #2: Properly deduplicates rows by date and prevents duplication when
   * editing existing dates. The deduplication ensures that if a date appears multiple times
   * (from both API-persisted and locally-added rows), only the most recent entry is kept.
   */
  const handleReviewReady = (updatedRows: ReviewRow[]) => {
    // Deduplicate by date — when the user accumulates bids across multiple
    // "Next" clicks the same date can appear in more than one bid; keep only
    // the last (most-recently-entered) row for each date.
    const seenDates = new Set<string>()
    const dedupedRows = [...updatedRows]
      .reverse()
      .filter((r) => {
        if (!r.date || seenDates.has(r.date)) return false
        seenDates.add(r.date)
        return true
      })
      .reverse()

    setLocalTableRows((prev) => {
      const affectedDates = new Set(dedupedRows.map((r) => r.date))
      // Keep data rows for dates not being updated; drop the old total row.
      // This ensures that when editing a date, the new version replaces the old one.
      const kept = prev.filter(
        (r) => r.id !== 'total' && !affectedDates.has(r.date)
      )
      // Sort so newly-added/edited dates fall into chronological order
      // alongside existing rows, instead of being appended at the end.
      const allData = sortRowsByDate([...kept, ...dedupedRows])
      const totalHours = allData.reduce(
        (sum, r) => sum + Number(r.requestedHours || 0),
        0
      )
      // Vacation Bid rows carry a checkbox (boolean), not hours to sum — legacy
      // leaves this cell blank on the total row for Vacation Bid requests.
      const totalOtherDisplay = isVacBid
        ? ''
        : formatHours(
            allData.reduce(
              (sum, r) => sum + getDisplayedOtherHoursTotal(String(r.hoursInOtherRequests || '')),
              0
            )
          )
      return [
        ...allData,
        {
          id: 'total',
          requestType: 'Total Hours',
          date: '',
          day: '',
          times: '',
          requestedHours: formatHours(totalHours),
          hoursInOtherRequests: totalOtherDisplay,
        },
      ]
    })
    setIsAddingMoreDates(false)
    if (isVacBid) {
      setShowCalendar(false)
      setIsEditPanelOpen(false)
    }
  }

  const [editSessionId, setEditSessionId] = useState(0)

  /** Normalizes a UI date string to a stable YYYY-MM-DD key for comparisons. */
  const toDateKey = useCallback((date: string): string => {
    if (!date) return ''
    return convertMMDDYYYYToYYYYMMDD(date) || date
  }, [])

  /** True when two date strings represent the same calendar day. */
  const isSameCalendarDate = useCallback(
    (left: string, right: string): boolean => toDateKey(left) === toDateKey(right),
    [toDateKey]
  )

  const handleTableEdit = useCallback(
    (date: string) => {
      if (!date) return
      setValidationMessages(null)

      if (isAddTimeReviewVisible) {
        setExitAddTimeReviewSignal((count) => count + 1)
      }

      setPendingRemoveDate(null)
      setFormDates([date])
      setIsEditPanelOpen(true)
      setShowCalendar(true)
      // Editing a single row must always lock the calendar to that one date,
      // even if the user had clicked "Add date to request" earlier in the
      // session without saving — otherwise isEditMode below stays false and
      // other dates remain clickable while "editing".
      setIsAddingMoreDates(false)
      setEditSessionId((count) => count + 1)
    },
    [isAddTimeReviewVisible]
  )

  /**
   * Removes a vacation-bid date from the local "Current Request" table only —
   * no API call is made here. The removal takes effect the next time the
   * user clicks "Save as Draft" or "Submit Vacation Bid", which sends
   * whatever dates remain in the table at that point (see
   * `submitEmbeddedVacBid`).
   *
   * Removing the LAST remaining date would leave an empty request, so
   * instead of removing it locally we open the Delete Request modal —
   * deleting the whole draft is the correct outcome for removing the last
   * remaining day.
   */
  const handleRemoveVacationBidDate = useCallback(
    (date: string) => {
      const activeRowCount = localTableRows.filter(
        (row) => row.id !== 'total' && row.date
      ).length

      // Removing the final row should confirm deleting the whole draft.
      if (activeRowCount <= 1) {
        setShowDeleteModal(true)
        return
      }

      setLocalTableRows((prev) => {
        const dataRows = prev.filter(
          (row) =>
            row.id !== 'total' &&
            row.date &&
            !isSameCalendarDate(String(row.date), date)
        )
        const totalHours = dataRows.reduce(
          (sum, row) => sum + Number(row.requestedHours || 0),
          0
        )
        return [
          ...dataRows,
          {
            id: 'total',
            requestType: 'Total Hours',
            date: '',
            day: '',
            times: '',
            requestedHours: formatHours(totalHours),
            // Vacation Bid rows carry a checkbox (boolean), not hours to sum
            // — legacy leaves this cell blank on the total row.
            hoursInOtherRequests: '',
          },
        ]
      })

      // Removing from the Current Request table should return the vacation-bid
      // right panel to its neutral comment/actions state rather than reopening
      // a populated entry form for any previously selected date.
      setFormDates([])
      setIsEditPanelOpen(false)
      setIsAddingMoreDates(false)
      setValidationMessages(null)

      // Track the removal locally so the calendar (displayHighlightedDates)
      // and committedDates stop treating this date as part of the request —
      // `data` itself won't reflect the removal until the next Save + backend
      // refetch. Mirrors handleRemoveDateFromDraft's non-vac-bid removal.
      setRemovedDates((prev) => (prev.includes(date) ? prev : [...prev, date]))
    },
    [localTableRows, isSameCalendarDate]
  )

  const handleTableRemove = useCallback(
    (date: string) => {
      if (!date) return
      setValidationMessages(null)

      if (isVacBid) {
        handleRemoveVacationBidDate(date)
        return
      }

      setPendingRemoveDate(date)
      setFormDates([date])
      if (isAddTimeReviewVisible) {
        setExitAddTimeReviewSignal((count) => count + 1)
      }
    },
    [isVacBid, isAddTimeReviewVisible, handleRemoveVacationBidDate]
  )

  const tableRowsWithActions = useMemo(() => {
    return localTableRows.map((row) => {
      if (row.id === 'total' || !row.date) return row

      return {
        ...row,
        edit: (
          <button
            type="button"
            aria-label={`Edit ${row.date}`}
            onClick={() => handleTableEdit(row.date)}
            className="flex items-center justify-center"
          >
            <img
              src="/assets/images/pencil-outline.svg"
              alt="edit"
              className="w-5 h-5"
            />
          </button>
        ),
        remove: (
          <button
            type="button"
            aria-label={`Remove ${row.date}`}
            onClick={() => handleTableRemove(row.date)}
            className="flex items-center justify-center"
          >
            <img
              src="/assets/images/minus-circle-outline.svg"
              alt="remove"
              className="w-5 h-5"
            />
          </button>
        ),
      }
    })
  }, [localTableRows, handleTableEdit, handleTableRemove])

  /**
   * Builds and submits (or saves as draft) the full vacation-bid draft,
   * merging all previously-saved dates, locally-added dates, and the currently-edited
   * date into a single request payload. This ensures that re-saving never drops dates
   * that are not currently open in the form.
   *
   * ISSUE #1 FIX: Properly merges all dates (existing API + locally-added + currently editing)
   * into a single request so no dates are lost when submitting after "Add More Dates".
   * ISSUE #3 FIX: Uses `initialVacHoursByDate` which includes locally-added rows,
   * ensuring newly-entered hours are preserved when editing before API save.
   *
   * No delete API call is made — `toReqId` is forwarded so the backend
   * associates the new record with the existing draft.
   *
   * @param comments  - User-entered comment string from the embedded form.
   * @param status    - 'DRAFT' to save without submitting; 'SUBMITTED' to submit.
   * @param formRank  - Choice/rank selected in the embedded form. Takes precedence
   *                    over `data.requestRank` so rank edits are preserved on save.
   */
  const submitEmbeddedVacBid = useCallback(
    async (
      comments: string,
      status: 'SUBMITTED' | 'DRAFT',
      formRank?: number
    ) => {
      // formDates holds all dates currently selected (potentially > 1 for
      // vacbids). It's empty when saving/submitting straight from landing on
      // the draft with no new date picked — that's fine as long as the
      // request already has dates (existing/locally-added), checked below
      // once those are collected.
      if (!data.requestId) return
      const vacDatesSet = new Set(formDates)

      // ── Dates currently in the "Current Request" table ─────────────────
      // `localTableRows` is the single source of truth for what belongs to
      // this draft: it starts from the API-persisted dates and is updated
      // in place for every local add (handleReviewReady) or remove
      // (handleRemoveVacationBidDate) made during the session. Neither of
      // those touch the backend — the table is only synced to the API here,
      // when the user explicitly saves/submits.
      const tableDates: string[] = localTableRows
        .filter((r) => r.id !== 'total' && Boolean(r.date))
        .map((r) => r.date as string)

      // Only use dates confirmed through "Next: Review Request" (in localTableRows).
      // Calendar selections still in-progress (formDates not yet in localTableRows)
      // must not be saved — this prevents unreviewed picks from being included
      // when "Cancel Request" → "Save as Draft" is clicked.
      const allMmddyyyy = Array.from(new Set(tableDates))
      // Nothing to save/submit — no dates exist yet anywhere (table or the
      // current form selection).
      if (allMmddyyyy.length === 0) return
      const allApiDates = allMmddyyyy.map(convertMMDDYYYYToYYYYMMDD)

      // ── Build per-date hours map ───────────────────────────────────────────
      // For dates in the current form selection, read hours from localTableRows
      // (populated by handleReviewReady after the user clicks "Next").
      // For all other dates fall back to initialVacHoursByDate which includes both
      // API-saved and locally-reviewed hours (ISSUE #3: ensures newly-entered hours are used).
      const perDateHoursValues: Record<string, HoursValues> = {}
      allMmddyyyy.forEach((mmddyyyy) => {
        const apiDate = convertMMDDYYYYToYYYYMMDD(mmddyyyy)
        if (vacDatesSet.has(mmddyyyy)) {
          // Currently being edited in the form: pull hours from localTableRows
          const localRow = localTableRows.find((r) => r.date === mmddyyyy)
          const editedHours = localRow ? String(localRow.requestedHours) : '8'
          perDateHoursValues[apiDate] = {
            vacation: editedHours,
            float: '0',
            flexible: '0',
            other: '0',
          }
        } else {
          // Not currently being edited: use initialVacHoursByDate which includes
          // both API-saved and locally-added-but-unsaved hours. This ensures that
          // when the user adds dates A, B, C, saves B, then adds D and saves,
          // the payload includes A, B, C, D correctly (ISSUE #1 fix).
          const savedHours = initialVacHoursByDate[mmddyyyy] ?? '8'
          perDateHoursValues[apiDate] = {
            vacation: savedHours,
            float: '0',
            flexible: '0',
            other: '0',
          }
        }
      })

      // Global hoursValues fallback — used when perDateHoursValues misses a date.
      // Pull from the first currently-selected date's local row.
      const firstLocalRow = localTableRows.find((r) =>
        formDates.includes(r.date as string)
      )
      const fallbackHours = firstLocalRow
        ? String(firstLocalRow.requestedHours)
        : '8'

      // Preserve an absent rank instead of converting the displayed NA value to rank 1.
      const torRank = formRank ?? data.requestRank ?? null

      setIsSubmitting(true)
      const result = await submitVacationBid(
        {
          selectedDates: allApiDates,
          // Global hoursValues used as fallback when perDateHoursValues does not cover a date.
          hoursValues: {
            vacation: fallbackHours,
            float: '0',
            flexible: '0',
            other: '0',
          },
          duration: 'Full Day',
          comments,
          torRank,
          // Forward the existing draft ID so the backend can update/replace it.
          toReqId: data.requestId,
          perDateHoursValues,
        },
        status
      )

      // ── Handle result ──────────────────────────────────────────────────────
      setIsSubmitting(false)
      if (!result.success) {
        setValidationMessages([
          String(result.error ?? 'Failed to save request. Please try again.'),
        ])
        return
      }

      setIsEditPanelOpen(false)
      setValidationMessages(null)

      const returnedId = result.timeOffRequestId || result.toReqId || ''

      if (status === 'SUBMITTED') {
        if (returnedId) {
          navigate?.(`/requestor/status/${returnedId}`)
        } else {
          navigate?.('/requestor/vacation-bid', {
            state: { showSubmittedBanner: true },
          })
        }
      } else {
        // Reload the status page so the updated draft data is visible.
        const draftId = returnedId || data.requestId
        navigate?.(`/requestor/status/${draftId}`)
      }
    },
    [
      formDates,
      data.requestId,
      data.requestRank,
      localTableRows,
      initialVacHoursByDate,
      submitVacationBid,
      navigate,
      setIsSubmitting,
    ]
  )

  /**
   * Removes the single date currently open in the edit form from the local
   * "Current Request" table only — no API call is made here. The removal
   * takes effect the next time the user clicks "Save as Draft" or "Submit
   * Request", which sends whatever dates remain in the table at that point
   * (see `preservedDatesPayload` above, merged into the payload by
   * AddTimeToRequest's `buildSubmissionPayload`).
   *
   * When the open date is the draft's ONLY date, removing it would leave an
   * empty request, so we open the Delete Request modal instead — deleting the
   * whole draft is the correct outcome for removing the last remaining day.
   */
  const handleRemoveDateFromDraft = useCallback(() => {
    if (formDates.length !== 1) return
    const dateToRemove = formDates[0]

    const activeRowCount = localTableRows.filter(
      (row) => row.id !== 'total' && row.date
    ).length

    if (activeRowCount <= 1) {
      setShowDeleteModal(true)
      return
    }

    setLocalTableRows((prev) => {
      const dataRows = prev.filter(
        (row) =>
          row.id !== 'total' &&
          row.date &&
          !isSameCalendarDate(String(row.date), dateToRemove)
      )
      const totalHours = dataRows.reduce(
        (sum, row) => sum + Number(row.requestedHours || 0),
        0
      )
      const totalOtherDisplay = formatHours(
        dataRows.reduce(
          (sum, row) =>
            sum + getDisplayedOtherHoursTotal(String(row.hoursInOtherRequests || '')),
          0
        )
      )
      return [
        ...dataRows,
        {
          id: 'total',
          requestType: 'Total Hours',
          date: '',
          day: '',
          times: '',
          requestedHours: formatHours(totalHours),
          hoursInOtherRequests: totalOtherDisplay,
        },
      ]
    })

    // Track the removal locally so the calendar (displayHighlightedDates) and
    // committedDates stop treating this date as part of the request — `data`
    // itself won't reflect the removal until the next Save + backend refetch.
    setRemovedDates((prev) =>
      prev.includes(dateToRemove) ? prev : [...prev, dateToRemove]
    )

    setFormDates([])
    setIsEditPanelOpen(false)
    setValidationMessages(null)
  }, [formDates, localTableRows, isSameCalendarDate])

  /**
   * Batch-removes an arbitrary set of dates from the local table and the
   * in-progress selection — no API call, same "takes effect on next
   * Save/Submit" contract as handleRemoveDateFromDraft above.
   *
   * Wired to AddTimeToRequest's onRemoveDatesClick, which its "Req 3
   * deselect" (handleDurationChange) fires when the user switches Duration
   * to Full Day: any OTHER selected date that only qualifies as Partial Day
   * because of an existing external partial-day request (see
   * otherRequestsPartialDayDates above) gets dropped. Unlike
   * handleRemoveDateFromDraft, which only ever acts on the single actively-
   * open date, this must remove dates other than the one currently being
   * edited without disturbing it — so it can't reuse that function.
   */
  const handleRemoveDatesFromDraft = useCallback((dates: string[]) => {
    if (dates.length === 0) return
    const toRemove = new Set(dates.map((date) => toDateKey(date)))

    setLocalTableRows((prev) => {
      const dataRows = prev.filter(
        (row) =>
          row.id !== 'total' &&
          row.date &&
          !toRemove.has(toDateKey(String(row.date)))
      )
      const totalHours = dataRows.reduce(
        (sum, row) => sum + Number(row.requestedHours || 0),
        0
      )
      const totalOtherDisplay = formatHours(
        dataRows.reduce(
          (sum, row) =>
            sum + getDisplayedOtherHoursTotal(String(row.hoursInOtherRequests || '')),
          0
        )
      )
      return [
        ...dataRows,
        {
          id: 'total',
          requestType: 'Total Hours',
          date: '',
          day: '',
          times: '',
          requestedHours: formatHours(totalHours),
          hoursInOtherRequests: totalOtherDisplay,
        },
      ]
    })

    setRemovedDates((prev) => Array.from(new Set([...prev, ...dates])))
    setFormDates((prev) => prev.filter((d) => !toRemove.has(toDateKey(d))))
  }, [toDateKey])

  const handleAddMoreDates = () => {
    setShowCalendar(true)
    setIsEditPanelOpen(false)
    setIsAddingMoreDates(true)
    if (isAddTimeReviewVisible) {
      setExitAddTimeReviewSignal((count) => count + 1)
      setIsAddTimeReviewVisible(false)
    }
    setFormDates([])
    setValidationMessages(null)
  }
  useEffect(() => {
    if (!pendingRemoveDate || isVacBid) return
    if (formDates.length !== 1 || formDates[0] !== pendingRemoveDate) return

    handleRemoveDateFromDraft()
    setPendingRemoveDate(null)
  }, [pendingRemoveDate, isVacBid, formDates, handleRemoveDateFromDraft])

  /**
   * Selects the correct right-panel content based on:
   * 1. Whether a date has been clicked (formDate is set)
   * 2. Whether the request is a vacation bid (isVacBid)
   * Forms are only shown when status is DRAFT (enforced via onDateClick below).
   */
  const renderRightPanel = () => {
    // Non-draft (read-only) vacation bid views still need a date clicked
    // before showing anything. Draft vacation bids fall through to
    // EmbeddedVacationBidForm below even with no date selected, so its
    // comment section + Cancel / Save as Draft / Submit buttons are visible
    // as soon as the draft loads.
    if (isVacBid && formDates.length === 0 && !isDraft) {
      return (
        <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
          View request details by clicking a specific date in the request.
        </p>
      )
    }

    if (isVacBid) {
      return (
        <div>
          {validationMessages && validationMessages.length > 0 && (
            <div className="space-y-3 mb-4">
              {validationMessages.map((msg) => (
                <ValidationBanner
                  key={msg}
                  variant={getValidationBannerVariant(msg)}
                  heading={getDraftValidationBannerHeading(msg)}
                  message={getDraftValidationBannerMessage(msg)}
                  compact={!isExpandedVacBidValidationBanner(msg)}
                />
              ))}
            </div>
          )}
          <EmbeddedVacationBidForm
            key={editSessionId}
            selectedDates={formDates}
            onCancelRequest={handleFormClose}
            onReviewReady={handleReviewReady}
            hideReviewTable
            initialHoursByDate={initialVacHoursByDate}
            initialRank={activeVacationBidRank}
            initialComments={data.employeeComments}
            currentRequestId={data.requestId}
            isSubmitting={isSubmitting}
            onSaveDraft={(comments, rank) => {
              submitEmbeddedVacBid(comments, 'DRAFT', rank)
            }}
            onSubmitRequest={(comments, rank) => {
              submitEmbeddedVacBid(comments, 'SUBMITTED', rank)
            }}
            existingDraftDates={nonDeselectableDates}
          />
        </div>
      )
    }

    const shouldShowFormPrompt = formDates.length === 0 || !isEditPanelOpen

    return (
      <div>
        {shouldShowFormPrompt && (
          <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
            {isDraft && isAddingMoreDates
              ? 'Select a date on the calendar to add time to your request'
              : ''}
          </p>
        )}
        {validationMessages && validationMessages.length > 0 && (
          <div className="space-y-3 mb-4">
            {validationMessages.map((msg) => (
              <ValidationBanner
                key={msg}
                variant={getValidationBannerVariant(msg)}
                heading={getDraftValidationBannerHeading(msg)}
                message={getDraftValidationBannerMessage(msg)}
                compact
              />
            ))}
          </div>
        )}
        {/* Visible whenever a date is actively being edited AND whenever no
            date is selected at all — the latter covers both landing on the
            draft and mid "Add More Dates" before a new date is picked, since
            in both cases AddTimeToRequest itself shows the comment section +
            Cancel / Save as Draft / Submit Request buttons immediately
            (hideReviewTable + no selectedDates), so the user can always
            save/submit the draft as-is instead of being blocked on picking a
            date first. Only hidden in the narrow window where a date HAS
            been picked but the edit panel hasn't opened for it yet. */}
        <div className={isEditPanelOpen || formDates.length === 0 ? '' : 'hidden'}>
          <AddTimeToRequest
            key={editSessionId}
            selectedDates={formDates}
            committedDates={nonDeselectableDates}
            onCancelRequest={handleFormClose}
            hideReviewTable
            // AddTimeToRequest only renders its input fields once isInDateEditMode
            // (mirrored from the editMode prop) is true — otherwise, while adding
            // more dates, it stays on its own "Select a date..." placeholder even
            // after dates have been picked. Flip it on as soon as a date is
            // selected during an Add More session; editDate tracks the most
            // recently added date as the active one being filled in.
            editMode={isAddingMoreDates && isEditPanelOpen}
            editDate={formDates.at(-1) ?? null}
            onReviewReady={handleReviewReady}
            onValidationError={setValidationMessages}
            exitAddTimeReviewSignal={exitAddTimeReviewSignal}
            onAddTimeReviewChange={setIsAddTimeReviewVisible}
            toReqId={data.requestId}
            initialHoursByDate={initialHoursByDate}
            initialOtherSubTypeByDate={initialOtherSubTypeByDate}
            initialDurationByDate={initialDurationByDate}
            initialTimeByDate={initialTimeByDate}
            initialComments={data.employeeComments}
            preservedDatesPayload={preservedDatesPayload}
            onRemoveDate={handleRemoveDateFromDraft}
            onRemoveDatesClick={handleRemoveDatesFromDraft}
            isRemovingDate={isSubmitting}
            isExempt={isExempt}
            isAddingMoreDates={isAddingMoreDates}
            otherRequestHoursByDate={otherRequestHoursByDate}
            otherPartialDayDates={otherPartialDayDatesForForm}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      {isSubmitting && <Loader />}
      <div ref={printRef}>
        <h1 className="h1">
          {isVacBid ? 'Draft Vacation Bid Request' : 'Draft Time Off Request'}
        </h1>
        <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row">
          <div className="flex-1">
            {(data.savedOn ?? data.submittedOn) && (
              <div className="mb-4">
                <LabeledValue
                  label="Saved On"
                  value={data.savedOn ?? data.submittedOn ?? ''}
                />
              </div>
            )}
            {/* Vacation bids require a visible rank even when the API has no rank value. */}
            {isVacBid && (
              <div className="mb-4">
                <LabeledValue label="Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} />
              </div>
            )}
            <div className="mb-4">
              <p className="p3 -medium">
                This time off request has been saved as a draft. If this draft
                is not submitted before the last day of the request, it will be
                deleted.
              </p>
            </div>
            <div className="mt-8!">
              <button type="button"
                className="button -danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Request
              </button>
            </div>
          </div>
          <div className="flex-1 flex flex-col">
            <div className="p-4! bg-[#F2F8FB] rounded-3xl">
              <div className="mb-6">
                <LabeledValue
                  label="Time Off Request for"
                  value={data.nuid ? `${displayName}` : displayName}
                />
              </div>
            </div>
            <div className="flex justify-start mt-4!">
              <button type="button"
                className="button -primary flex items-center gap-1"
                onClick={handlePrint}
              >
                <img
                  src="/assets/images/printer-outline.svg"
                  alt="Print"
                  className="w-7 h-7"
                />
                <span className="align-middle">Print</span>
              </button>
            </div>
          </div>
        </div>
        <div className="mt-6!">
          <hr className="-variation-1" />
        </div>
        {data.history && data.history.length > 0 && (
          <HistorySection history={data.history} />
        )}
        <CurrentRequestSection
          isDraft={isDraft}
          tableRows={tableRowsWithActions}
          highlightedDates={displayHighlightedDates}
          committedDates={nonDeselectableDates}
          onDateClick={isDraft ? handleDateClick : undefined}
          rightPanel={renderRightPanel()}
          canEnterEditMode={isEditPanelOpen || isAddTimeReviewVisible || isDraft}
          isEditMode={isEditPanelOpen && !!formDates[0] && !isAddingMoreDates}
          editDate={formDates[0]}
          editModeSelectedDates={formDates}
          // Existing (committed) request dates must stay unclickable for the
          // entire "Add date to request" session — editing an existing date
          // goes through the table's pencil icon, not the calendar. Gating
          // this on isEditPanelOpen instead of isAddingMoreDates let committed
          // dates become clickable again as soon as the first new date was
          // picked, which incorrectly let users reopen existing dates from
          // the calendar mid-session.
          disableCommittedDateClick={isAddingMoreDates}
          // Exception to the block above: a committed date that's already
          // Partial Day still has unused hours in the day, so let the user
          // click it to add the remaining time as a new entry.
          unblockedCommittedDates={partialDayDatesWithRoom}
          tableColumns={currentRequestTableColumns}
          renderCell={
            isVacBid
              ? (row: any, col: { key: string }) => {
                  if (col.key !== 'hoursInOtherRequests') return row[col.key]
                  if (row.id === 'total') return ''
                  return (
                    <input
                      type="checkbox"
                      className="table-cell-checkbox"
                      checked={isVacBidDayInOtherRequestsChecked(
                        otherReqData,
                        row.date
                      )}
                      disabled
                      readOnly
                      aria-label="Day in other requests"
                    />
                  )
                }
              : undefined
          }
          showCalendar={showCalendar && !isAddTimeReviewVisible}
          highlightVacationBidOnHold={isVacBid}
          calendarWidth={372}
          calendarHeight={399}
          selectedDatesForCalendar={
            formDates.length > 0 ? formDates : displayHighlightedDates
          }
          onAddMoreDates={handleAddMoreDates}
          excludeRequestId={data.requestId}
        />
      </div>
      <Modal
        isOpen={showDeleteModal}
        title="Delete Request"
        onClose={() => setShowDeleteModal(false)}
      >
        <div className="flex items-center gap-3 text-left">
          <img
            src="/assets/images/alertSolid.svg"
            alt="Alert"
            className="w-8 h-8"
          />
          <div className="p3 -medium">
            You have selected this time off request for deletion.
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6!">
          <button type="button"
            className="button -secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button -danger"
            onClick={handleDeleteRequest}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Yes, Delete Draft Request'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default DraftView
